import { api } from './client'

// The History section of an audited record's detail page (00 App Shell §Record history, #69):
// its audit_logs entries, newest first. Owner only — the API answers Staff with 403, so the hooks
// only run for the Owner.
//
// Each record has its own endpoint, and each one's generated type is only as wide as its contract
// examples; every one of them is assignable to this shape, which the component renders. A field
// whose contract type is only `null` on some endpoint (`reason` on the catalog records, `subject`
// and a change's `label` outside P-05 / P-07 / P-09) is dropped from that endpoint's generated
// type, so those three are optional here.
export type AuditLogEntry = {
  id: number
  action: string
  reason?: string | null
  created_at: string
  user: { id: number; email: string; name: string | null } | null
  // What the entry is about when the record it is listed on is not the record itself: a color
  // slot (#105) — a slot's archive entry in its Model's History, or a Variant's Pool remap — or a
  // Payment listed in its Sales Report's History (06d #187), which has no `model`. Null otherwise.
  subject?: { type: string; model?: string | null; slot: string } | null
  // `label` names the slot of a Recipe edit's est. grams change (#105); null on other changes.
  changes: { field: string; label?: string | null; before: string | null; after: string | null }[]
}

export type AuditedRecord =
  | 'pools'
  | 'restocks'
  | 'adjustments'
  | 'opening_balances'
  | 'products'
  | 'models'
  | 'model_versions'
  | 'variants'
  | 'batches'
  | 'assemblies'
  | 'machines'
  | 'sales_reports'
  | 'expenses'

// Query keys, for the mutations that write an audit entry (key edit, archive, void, note edit).
export const AUDIT_LOG_KEYS = {
  pools: ['get', '/api/w/{slug}/pools/{pool_id}/audit_logs'],
  restocks: ['get', '/api/w/{slug}/restocks/{restock_id}/audit_logs'],
  adjustments: ['get', '/api/w/{slug}/adjustments/{adjustment_id}/audit_logs'],
  opening_balances: ['get', '/api/w/{slug}/opening_balances/{opening_balance_id}/audit_logs'],
  products: ['get', '/api/w/{slug}/products/{product_id}/audit_logs'],
  models: ['get', '/api/w/{slug}/models/{model_id}/audit_logs'],
  model_versions: ['get', '/api/w/{slug}/model_versions/{model_version_id}/audit_logs'],
  variants: ['get', '/api/w/{slug}/variants/{variant_id}/audit_logs'],
  batches: ['get', '/api/w/{slug}/batches/{batch_id}/audit_logs'],
  assemblies: ['get', '/api/w/{slug}/assemblies/{assembly_id}/audit_logs'],
  machines: ['get', '/api/w/{slug}/machines/{machine_id}/audit_logs'],
  sales_reports: ['get', '/api/w/{slug}/sales_reports/{sales_report_id}/audit_logs'],
  expenses: ['get', '/api/w/{slug}/expenses/{expense_id}/audit_logs'],
} as const

type Options = { enabled: boolean }

export function usePoolAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/pools/{pool_id}/audit_logs',
    { params: { path: { slug, pool_id: id } } },
    { retry: false, enabled },
  )
}

export function useRestockAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/restocks/{restock_id}/audit_logs',
    { params: { path: { slug, restock_id: id } } },
    { retry: false, enabled },
  )
}

export function useAdjustmentAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/adjustments/{adjustment_id}/audit_logs',
    { params: { path: { slug, adjustment_id: id } } },
    { retry: false, enabled },
  )
}

export function useOpeningBalanceAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/opening_balances/{opening_balance_id}/audit_logs',
    { params: { path: { slug, opening_balance_id: id } } },
    { retry: false, enabled },
  )
}

export function useProductAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/products/{product_id}/audit_logs',
    { params: { path: { slug, product_id: id } } },
    { retry: false, enabled },
  )
}

// A Model's History lists its color slots' archive entries too (P-05).
export function useModelAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/models/{model_id}/audit_logs',
    { params: { path: { slug, model_id: id } } },
    { retry: false, enabled },
  )
}

export function useModelVersionAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/model_versions/{model_version_id}/audit_logs',
    { params: { path: { slug, model_version_id: id } } },
    { retry: false, enabled },
  )
}

// On P-09 until P-10 ships with 05 (#98 Q16).
export function useVariantAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/variants/{variant_id}/audit_logs',
    { params: { path: { slug, variant_id: id } } },
    { retry: false, enabled },
  )
}

// The 04 Manufacturing records (#137): F-06, F-11 and F-03.
export function useBatchAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/batches/{batch_id}/audit_logs',
    { params: { path: { slug, batch_id: id } } },
    { retry: false, enabled },
  )
}

export function useAssemblyAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/assemblies/{assembly_id}/audit_logs',
    { params: { path: { slug, assembly_id: id } } },
    { retry: false, enabled },
  )
}

export function useMachineAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/machines/{machine_id}/audit_logs',
    { params: { path: { slug, machine_id: id } } },
    { retry: false, enabled },
  )
}

// The 06 Sales records (06d #187): the Sales Report (S-07), whose History also lists its
// Payments' voids — a Payment has no screen of its own.
export function useSalesReportAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/sales_reports/{sales_report_id}/audit_logs',
    { params: { path: { slug, sales_report_id: id } } },
    { retry: false, enabled },
  )
}

// The 07 Expenses record (07d #197): an Expense, whose History sits on the E-02 edit screen since
// an Expense has no detail screen of its own. A Restock Expense's History carries the void of the
// Restock that voided it.
export function useExpenseAuditLogs(slug: string, id: number, { enabled }: Options) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/expenses/{expense_id}/audit_logs',
    { params: { path: { slug, expense_id: id } } },
    { retry: false, enabled },
  )
}
